// Single liveUpdated SSE subscription (Go AGENTS.md §15 / React AGENTS.md).
// Entity arms merge into normalized Apollo cache entries so admin grids patch
// row cells in place. Soft-evict list roots only when membership changes
// (creates) or when derived list shape cannot be patched from the payload.
import { useApolloClient, useSubscription } from '@apollo/client/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { graphql } from '@/gql';
import { NightEntranceInsightsDoc } from '../features/nights/entranceInsights';
import { isAuthenticated } from '../api/auth';
import { useDocumentVisible } from './useDocumentVisible';
import { ActiveRecognitionDayDoc } from '../features/session/useRecognitionDayOpen';
import { useSession } from '../features/session/useSession';
import { hasPermission } from '../lib/permissions';

const LiveUpdatedDoc = graphql(/* GraphQL */ `
  subscription LiveUpdated($scopeId: UUID!) {
    liveUpdated(scopeId: $scopeId) {
      payment {
        intent
        payment {
          id
          status
          paymentMethod
          amount
          amountTip
          updatedAt
          message
          cardAlias
        }
      }
      refund {
        intent
        refund {
          id
          status
          paymentMethod
          amount
          message
          createdAt
          updatedAt
          recognitionDate
          order {
            id
          }
          tab {
            id
            name
          }
          payment {
            id
            status
          }
          terminal {
            id
            name
          }
        }
      }
      order {
        intent
        timestamp
        order {
          id
          status
          paymentStatus
          paymentMethod
          updatedAt
          grandTotal
          amountPaid
          amountDue
          allowedActions
        }
      }
      orderCreated {
        id
        status
        paymentStatus
        paymentMethod
        createdAt
        updatedAt
        grandTotal
        amountPaid
        amountDue
        allowedActions
      }
      tab {
        intent
        tab {
          id
          name
          status
          updatedAt
          grandTotal
          amountPaid
          amountDue
          canEditLineItems
          allowedActions
        }
      }
      category {
        id
        name
        isActive
        showInMenu
        productCount
      }
      product {
        id
        name
        price
        tax
        isActive
      }
      discount {
        id
        name
        type
        isActive
        totalDiscount30d
        usageCount30d
      }
      table {
        id
        name
        isActive
      }
      device {
        intent
        device {
          id
          name
          status
          isActive
          connectionStatus
          allowedActions
          askLoyalty
          gratuitySettings {
            enabled
          }
          category {
            id
            name
          }
          terminal {
            id
            name
          }
        }
      }
      terminal {
        intent
        terminal {
          id
          name
          poiid
          isActive
          connected
          batteryLevel
          updatedAt
        }
      }
      ledger {
        intent
        timestamp
        transaction {
          id
          transactionId
          amountReceived
          amountTip
          amountCashTrade
          amountRecognizedTotal
          psp
          paymentMethod
          recognitionDate
          createdAt
          categoryName
          deviceName
          categoryId
          deviceId
        }
      }
      recognitionDay {
        intent
        session {
          id
          recognitionDate
          isOpen
          openedAt
          closedAt
        }
      }
      entranceInsights {
        nightId
        nightTitle
        rows {
          stageId
          label
          sold
          scanned
          admitted
          refused
        }
        total {
          stageId
          label
          sold
          scanned
          admitted
          refused
        }
      }
    }
  }
`);

/** Apollo operation names to refetch after soft-evicting a ROOT_QUERY field. */
const ROOT_FIELD_REFETCH_QUERIES: Record<string, string[]> = {
  orders: ['Orders'],
  payments: ['Payments'],
  refunds: ['Refunds'],
  tabs: ['Tabs'],
  products: ['Products'],
  terminals: ['Terminals'],
  ledgerTransactions: ['LedgerTransactions'],
  turnoverWeek: ['TurnoverWeek'],
  turnoverAvailableWeeks: ['TurnoverAvailableWeeks'],
};

function softEvictRootFields(
  client: ReturnType<typeof useApolloClient>,
  fields: string[],
  refetch = true,
): void {
  for (const field of fields) {
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: field });
  }
  client.cache.gc();
  if (!refetch) return;
  const include = fields.flatMap(
    (field) => ROOT_FIELD_REFETCH_QUERIES[field] ?? [],
  );
  if (include.length > 0) {
    void client.refetchQueries({ include });
  }
}

const ORDER_MEMBERSHIP_INTENTS = [
  'ORDER_CREATED',
  'ORDER_FULLY_PAID',
  'ORDER_PARTIALLY_PAID',
  'ORDER_FULLY_REFUNDED',
  'ORDER_PARTIALLY_REFUNDED',
  'ORDER_CANCELED',
  'ORDER_LOST',
  'ORDER_FAILED',
] as const;

const ORDER_TURNOVER_INTENTS = ['ORDER_CANCELED', 'ORDER_LOST'] as const;

const REFUND_MEMBERSHIP_INTENTS = [
  'REFUND_PENDING',
  'REFUND_SUCCEEDED',
  'REFUND_FAILED',
  'REFUND_CANCELED',
] as const;

const LEDGER_REFRESH_INTENTS = [
  'LEDGER_RECORDED',
  'LEDGER_UPDATED',
  'LEDGER_DELETED',
] as const;

/** First pause before a stream the server completed is refetched and reopened. */
export const STREAM_RESTART_DELAY_MS = 1000;
/**
 * Consecutive data-less completions after which the stream stays closed. Each
 * one doubles the pause: 1, 2, 4, 8, 16, 32 s with the value 6.
 */
export const STREAM_RESTART_MAX_ATTEMPTS = 6;
/**
 * A stream that stayed open at least this long before the server completed it
 * was healthy (a quiet night delivers no frames), so its completion does not
 * count as a failure. Longer than the longest restart pause (32 s), far shorter
 * than any server cap on a request.
 */
export const STREAM_HEALTHY_MS = 60_000;

/**
 * Opens a single liveUpdated subscription for the active scope.
 * Server filters arms by permission; recognitionDay writes ActiveRecognitionDay.
 *
 * The stream is skipped as soon as the tab is hidden, which disposes the active
 * SSE subscription immediately. On hidden -> visible the active queries are
 * refetched once (through the Apollo error link, so an expired access token is
 * refreshed first) and the stream is reopened after that refetch settles, so
 * the screen catches up on missed events. A successful refetch has refreshed
 * an expired token first, a rejected refresh logs out (the stream stays
 * closed), and a failed refetch reopens with the current token as before.
 *
 * A stream the server completes (Go does after answering UNAUTHENTICATED,
 * graphql-sse completes when it gives up, and Apollo also completes after any
 * stream error such as FORBIDDEN) is re-armed the same way: after a pause it is
 * disabled, which runs the refetch-then-reopen effect below. The pause starts at
 * STREAM_RESTART_DELAY_MS and doubles for every consecutive completion that
 * delivered no data (1, 2, 4, 8, 16, 32 s). After
 * STREAM_RESTART_MAX_ATTEMPTS such quick failures the stream stays closed until
 * the next hidden -> visible transition. The budget is per run of quick
 * failures: a stream that delivered a frame or stayed open for at least
 * STREAM_HEALTHY_MS before it completed resets it, and so do a hidden -> visible
 * transition and a scope change. A server that keeps failing therefore cannot
 * make a visible tab spin, and a healthy but quiet stream that the server ends
 * now and then is never given up on. A dead session has also cleared its token,
 * so nothing reopens.
 */
export function useLiveEvents(scopeId: string | undefined): void {
  const { me } = useSession();
  const client = useApolloClient();
  const perms = me?.permissions;
  const canSubscribe =
    !!scopeId && hasPermission(perms, ['pos:operate', 'scope:read']);

  const visible = useDocumentVisible();
  const [streamEnabled, setStreamEnabled] = useState(visible);
  const [prevVisible, setPrevVisible] = useState(visible);
  if (visible !== prevVisible) {
    setPrevVisible(visible);
    if (!visible) setStreamEnabled(false);
  }

  // streamEnabled is false while the tab is visible only after a hidden ->
  // visible transition or after the server completed the stream (see
  // onComplete below), so this never refetches on mount.
  useEffect(() => {
    if (!visible || streamEnabled) return;
    let cancelled = false;
    void (async () => {
      try {
        await client.refetchQueries({ include: 'active' });
      } catch {
        // Failed refetches surface on their own screens; still reopen below.
      }
      // A rejected refresh token logs out; do not open the stream after that.
      if (!cancelled && isAuthenticated()) setStreamEnabled(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, streamEnabled, client]);

  const restartTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const restartAttempts = useRef(0);
  const gotData = useRef(false);
  const openedAt = useRef(0);
  useEffect(() => () => clearTimeout(restartTimer.current), []);
  // Hidden: drop a pending restart; the next visible transition starts afresh.
  useEffect(() => {
    if (visible) return;
    clearTimeout(restartTimer.current);
    restartAttempts.current = 0;
    gotData.current = false;
  }, [visible]);
  // A new scope is a new stream: forget the old scope's failures.
  useEffect(() => {
    clearTimeout(restartTimer.current);
    restartAttempts.current = 0;
    gotData.current = false;
  }, [scopeId]);
  // Remember when the stream (re)opened, to tell a healthy one from a failure.
  useEffect(() => {
    if (streamEnabled && canSubscribe) openedAt.current = Date.now();
  }, [streamEnabled, canSubscribe, scopeId]);
  const onData = useCallback(() => {
    gotData.current = true;
  }, []);
  const onComplete = useCallback(() => {
    const healthy =
      gotData.current || Date.now() - openedAt.current >= STREAM_HEALTHY_MS;
    if (healthy) restartAttempts.current = 0;
    gotData.current = false;
    if (restartAttempts.current >= STREAM_RESTART_MAX_ATTEMPTS) return;
    const delay = STREAM_RESTART_DELAY_MS * 2 ** restartAttempts.current;
    restartAttempts.current += 1;
    clearTimeout(restartTimer.current);
    restartTimer.current = setTimeout(() => setStreamEnabled(false), delay);
  }, []);

  const { data } = useSubscription(LiveUpdatedDoc, {
    variables: { scopeId: scopeId! },
    skip: !canSubscribe || !streamEnabled,
    onData,
    onComplete,
  });

  // Deduplicate: Apollo may re-deliver the same result object on re-render.
  const lastHandled = useRef<unknown>(null);

  useEffect(() => {
    const live = data?.liveUpdated;
    if (!live || live === lastHandled.current) return;
    lastHandled.current = live;

    if (live.orderCreated) {
      softEvictRootFields(client, ['orders'], false);
    }

    if (live.order) {
      const { intent } = live.order;
      if (
        ORDER_MEMBERSHIP_INTENTS.includes(
          intent as (typeof ORDER_MEMBERSHIP_INTENTS)[number],
        )
      ) {
        softEvictRootFields(client, ['orders']);
      }
      // Tab order removal cancels the order and deletes its ledger row; refresh
      // turnover even if the ledger arm is sparse. ORDER_LOST is the End Day /
      // mark-loss twin of ORDER_CANCELED (same write-off + ledger delete).
      if (
        ORDER_TURNOVER_INTENTS.includes(
          intent as (typeof ORDER_TURNOVER_INTENTS)[number],
        )
      ) {
        softEvictRootFields(client, [
          'turnoverWeek',
          'turnoverAvailableWeeks',
          'ledgerTransactions',
        ]);
      }
    }

    if (live.payment?.intent === 'PAYMENT_PENDING') {
      softEvictRootFields(client, ['payments']);
    }

    // New refunds need list membership; terminal intents may leave a filtered page.
    if (
      live.refund &&
      REFUND_MEMBERSHIP_INTENTS.includes(
        live.refund.intent as (typeof REFUND_MEMBERSHIP_INTENTS)[number],
      )
    ) {
      softEvictRootFields(client, ['refunds']);
    }

    // PIN/Adyen may historically emit LEDGER_UPDATED on first write (reconcile);
    // deletes (remove order from tab / empty reprice) emit LEDGER_DELETED.
    if (
      live.ledger &&
      LEDGER_REFRESH_INTENTS.includes(
        live.ledger.intent as (typeof LEDGER_REFRESH_INTENTS)[number],
      )
    ) {
      softEvictRootFields(client, [
        'ledgerTransactions',
        'turnoverWeek',
        'turnoverAvailableWeeks',
      ]);
    }

    if (live.tab?.intent === 'TAB_OPENED') {
      softEvictRootFields(client, ['tabs']);
    }

    // Category product membership can change product.categories arrays.
    if (live.category) {
      softEvictRootFields(client, ['products'], false);
    }

    // Single terminal upserts via normalized cache; soft-evict list membership.
    if (live.terminal?.terminal) {
      softEvictRootFields(client, ['terminals']);
    }

    if (live.recognitionDay) {
      const { intent, session } = live.recognitionDay;
      const next =
        intent === 'RECOGNITION_DAY_ENDED' || !session.isOpen ? null : session;
      client.writeQuery({
        query: ActiveRecognitionDayDoc,
        data: { activeRecognitionDay: next },
      });
    }

    // Entrance insights are a full replace for one night: overwrite the
    // cached query result instead of evicting it, so the card never flickers.
    if (live.entranceInsights) {
      client.writeQuery({
        query: NightEntranceInsightsDoc,
        variables: { nightId: live.entranceInsights.nightId },
        data: { nightEntranceInsights: live.entranceInsights },
      });
      // The Turnover page's Today "Tickets scanned" tile counts the same
      // scans; refetch it (no evict, so the page stays put) while it is open.
      void client.refetchQueries({ include: ['TurnoverWeek'] });
    }
  }, [data, client]);
}
